import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import {
  HeartPulse, Mic, Sparkles, FileText, Share2, CalendarDays,
  Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, X, RotateCcw, Loader2,
} from "lucide-react";

const VISUALS = {
  welcome: { icon: HeartPulse, grad: "from-[#1E5AA8] to-[#123C73]" },
  record: { icon: Mic, grad: "from-rose-500 to-orange-400" },
  analyze: { icon: Sparkles, grad: "from-violet-500 to-indigo-500" },
  prescribe: { icon: FileText, grad: "from-emerald-500 to-teal-500" },
  share: { icon: Share2, grad: "from-amber-500 to-orange-500" },
  manage: { icon: CalendarDays, grad: "from-sky-500 to-blue-600" },
};

export default function DemoPlayer({ open, onClose }) {
  const [steps, setSteps] = useState([]);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [finished, setFinished] = useState(false);
  const audioRef = useRef(null);
  const playingRef = useRef(false);
  if (!audioRef.current && typeof Audio !== "undefined") audioRef.current = new Audio();

  useEffect(() => { playingRef.current = playing; }, [playing]);
  useEffect(() => { if (audioRef.current) audioRef.current.muted = muted; }, [muted]);

  const advance = () => {
    setIdx((i) => {
      if (i + 1 >= steps.length) { setPlaying(false); playingRef.current = false; setFinished(true); return i; }
      return i + 1;
    });
  };

  // Load script + auto-start when opened
  useEffect(() => {
    if (!open) { if (audioRef.current) audioRef.current.pause(); setPlaying(false); playingRef.current = false; return; }
    setIdx(0); setFinished(false);
    api.get("/demo/script").then((r) => {
      setSteps(r.data.steps || []);
      setPlaying(true); playingRef.current = true;
    }).catch(() => {});
  }, [open]);

  // Load + play audio for the current step
  useEffect(() => {
    if (!open || !steps[idx]) return;
    let cancelled = false;
    let url;
    (async () => {
      setLoading(true);
      try {
        const res = await api.post("/demo/tts", { text: steps[idx].caption }, { responseType: "blob" });
        if (cancelled) return;
        url = URL.createObjectURL(res.data);
        const a = audioRef.current;
        a.src = url;
        a.muted = muted;
        a.onended = advance;
        if (playingRef.current) {
          try { await a.play(); } catch { setPlaying(false); playingRef.current = false; }
        }
      } catch {
        // narration failed — auto-advance after a short pause so the slideshow still flows
        if (!cancelled && playingRef.current) setTimeout(advance, 4000);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
    // eslint-disable-next-line
  }, [idx, open, steps]);

  const toggle = async () => {
    const a = audioRef.current;
    if (finished) { setFinished(false); setIdx(0); setPlaying(true); playingRef.current = true; return; }
    if (playing) { a.pause(); setPlaying(false); playingRef.current = false; }
    else { setPlaying(true); playingRef.current = true; try { await a.play(); } catch {} }
  };

  const go = (n) => {
    const next = Math.max(0, Math.min(steps.length - 1, n));
    setFinished(false);
    setIdx(next);
    setPlaying(true); playingRef.current = true;
  };

  const close = () => { if (audioRef.current) audioRef.current.pause(); onClose(); };

  if (!open) return null;
  const step = steps[idx];
  const V = (step && VISUALS[step.id]) || VISUALS.welcome;
  const Icon = V.icon;

  return (
    <div data-testid="demo-player" className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm">
      <motion.div initial={{ opacity: 0, scale: 0.94, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }}
        className="w-full max-w-lg rounded-3xl bg-white shadow-2xl overflow-hidden border border-white">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <span className="w-7 h-7 rounded-lg bg-[#1E5AA8] flex items-center justify-center"><HeartPulse className="w-4 h-4 text-amber-300" /></span>
            <span className="font-display font-bold text-[#123C73]" data-notranslate>MDvia Care</span>
            <span className="text-xs text-slate-400">· Quick demo</span>
          </div>
          <button data-testid="demo-close" onClick={close} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Slide visual */}
        <div className={`relative h-52 sm:h-60 bg-gradient-to-br ${V.grad} flex items-center justify-center overflow-hidden`}>
          <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-white/15 blur-2xl" />
          <div className="absolute -bottom-10 -left-6 w-44 h-44 rounded-full bg-black/10 blur-2xl" />
          <AnimatePresence mode="wait">
            <motion.div key={step?.id || idx} initial={{ opacity: 0, scale: 0.6, rotate: -8 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }} exit={{ opacity: 0, scale: 0.6 }}
              transition={{ type: "spring", stiffness: 180, damping: 18 }}
              className="relative w-24 h-24 rounded-3xl bg-white/20 backdrop-blur flex items-center justify-center ring-1 ring-white/40">
              <Icon className="w-12 h-12 text-white" />
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Caption */}
        <div className="px-6 py-5 min-h-[92px] flex items-center">
          <AnimatePresence mode="wait">
            <motion.p key={idx} data-testid="demo-caption" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="text-slate-700 text-base leading-relaxed text-center w-full">
              {step?.caption || ""}
            </motion.p>
          </AnimatePresence>
        </div>

        {/* Progress dots */}
        <div className="flex items-center justify-center gap-1.5 pb-3">
          {steps.map((s, i) => (
            <span key={s.id} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-[#1E5AA8]" : "w-1.5 bg-slate-200"}`} />
          ))}
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-slate-50/60">
          <button data-testid="demo-mute" onClick={() => setMuted((m) => !m)}
            className="p-2 rounded-lg text-slate-500 hover:bg-white" title={muted ? "Unmute" : "Mute"}>
            {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
          </button>
          <div className="flex items-center gap-2">
            <button data-testid="demo-prev" onClick={() => go(idx - 1)} disabled={idx === 0}
              className="p-2 rounded-lg text-slate-500 hover:bg-white disabled:opacity-40"><SkipBack className="w-5 h-5" /></button>
            <button data-testid="demo-play-toggle" onClick={toggle}
              className="w-12 h-12 rounded-full bg-[#1E5AA8] text-white flex items-center justify-center shadow-lg hover:bg-[#123C73] transition-colors">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : finished ? <RotateCcw className="w-5 h-5" /> : playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
            </button>
            <button data-testid="demo-next" onClick={() => go(idx + 1)} disabled={idx >= steps.length - 1}
              className="p-2 rounded-lg text-slate-500 hover:bg-white disabled:opacity-40"><SkipForward className="w-5 h-5" /></button>
          </div>
          <button data-testid="demo-done" onClick={close} className="text-sm font-semibold text-[#1E5AA8] hover:underline px-2">Done</button>
        </div>
      </motion.div>
    </div>
  );
}
